import { useState } from 'react';
import {handleGetDownload} from "../downloads"

type DownloadHistoryUploadedAt =
  | string
  | number
  | Date
  | { _seconds?: number; _nanoseconds?: number }
  | { toDate: () => Date };

export interface DownloadHistoryItem {
  downloadURL: string;
  size?: number;
  storagePath?: string;
  uploadedAt?: DownloadHistoryUploadedAt;
}

export interface DownloadListItemProps {
  filename?: string;
  url?: string;
  uploadedAt?: string;
  description?: string;
  linkURL?: string;
  webIcon?: string;
  displayName?: string;
  currentVersion?: string;
  startDate?: string;
  endDate?: string;
  history?: DownloadHistoryItem[];
}

function getFileIcon(webIconPath?: string): string {
  if (!webIconPath) {
    return '';
  }
  return '/icons/' + webIconPath;
}

function parseUploadedAt(value?: DownloadHistoryUploadedAt): string {
  if (!value) {
    return "";
  }

  if (typeof value === "string") {
    return value;
  }

  if (typeof value === "number") {
    return new Date(value).toLocaleString();
  }

  if (value instanceof Date) {
    return value.toLocaleString();
  }

  if (typeof value === "object") {
    if (typeof (value as any).toDate === "function") {
      return (value as any).toDate().toLocaleString();
    }
    const seconds = (value as any)._seconds;
    const nanos = (value as any)._nanoseconds;
    if (typeof seconds === "number") {
      return new Date(seconds * 1000 + (nanos || 0) / 1_000_000).toLocaleString();
    }
  }

  return "";
}

function getHistoryFileName(storagePath?: string): string {
  if (!storagePath) {
    return "download";
  }
  const parts = storagePath.split("/");
  return parts[parts.length - 1] || storagePath;
}

function DownloadListItem(props: DownloadListItemProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const icon_path = getFileIcon(props.webIcon);

  const content = (
    <div className="w-full border border-light-border rounded p-4 flex flex-col gap-3 shadow-sm cursor-default">
      <div className="flex flex-row items-start gap-3">
        {icon_path !== '' && (
          <div className="text-sm shrink-0 self-center">
            <img
              src={icon_path}
              alt={
                props.filename
                  ? `${props.filename.split('.').pop()?.toUpperCase()} file icon`
                  : 'File icon'
              }
              className="w-8 h-8 text-sm"
            />
          </div>
        )}

        <div className="flex-1 min-w-0">
          <button>
            <div className="font-bold text-light-text-link truncate">
              {props.displayName || 'Untitled'}
            </div>
          </button>
          {props.uploadedAt && (
            <div className="text-sm text-light-text mt-1">
              Uploaded: {props.uploadedAt}
            </div>
          )}
          {props.description && (
            <div className="text-sm text-light-text mt-1 line-clamp-2">
              {props.description}
            </div>
          )}
        </div>
        {props.linkURL && (
          <button
            id={`ts-download-${props.filename}`}
            className="shrink-0 px-2 py-1 text-sm text-light-text-alt bg-light-main border border-light-border rounded hover:bg-light-main-hover duration-200 ease-in-out cursor-pointer"
            aria-label={`Download ${props.filename || 'item'}`}
            onClick={(_) => {
              handleGetDownload(props.linkURL || '');
            }}
          >
            Download
          </button>
        )}
      </div>

      <div className="flex justify-start">
        <button
          className="px-2 py-1 text-sm text-light-text-alt bg-light-main border border-light-border rounded hover:bg-light-main-hover duration-200 ease-in-out cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
          aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
        >
          {isExpanded ? 'Hide Versions' : 'Show Versions'}
        </button>
      </div>

      {isExpanded && (
        <div className="border-t border-light-border pt-3">
          <div className="space-y-3">
            {props.history && props.history.length > 0 ? (
              props.history
                .slice()
                .reverse()
                .slice(0, 9)
                .map((historyItem, index) => {
                  const uploadedAt = parseUploadedAt(historyItem.uploadedAt);
                  const fileName = getHistoryFileName(historyItem.storagePath);

                  return (
                    <div
                      key={historyItem.storagePath || index}
                      className="flex flex-col gap-2 p-3 rounded border border-light-border bg-slate-50"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <div>
                          <div className="text-sm font-medium text-light-text">
                            {uploadedAt || 'Unknown upload date'}
                          </div>
                        </div>
                        {historyItem.downloadURL ? (
                          <a
                            href={historyItem.downloadURL}
                            target="_blank"
                            rel="noreferrer noopener"
                            download={fileName}
                          >
                            <button
                              type="button"
                              className="px-3 py-1 text-sm text-light-text-alt bg-light-main border border-light-border rounded hover:bg-light-main-hover duration-200 ease-in-out"
                            >
                              Download
                            </button>
                          </a>
                        ) : (
                          <button
                            type="button"
                            disabled
                            className="px-3 py-1 text-sm text-light-text-alt bg-gray-200 border border-light-border rounded cursor-not-allowed"
                          >
                            No download link
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
            ) : (
              <div className="text-sm text-light-text">No history available.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  return content;
}

export default DownloadListItem;
